import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Grid, List, ListItem, ListItemIcon, ListItemText, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import {
  DialogComponent,
  FooterComponent,
  WizardComponent,
  DragToCloseModalComponent,
  IconCardComponent,
} from 'components';
import { formMapper } from 'utils';
import { FormattedMessage } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import SvgIcon from '@material-ui/core/SvgIcon';
import { faPlus } from '@fortawesome/pro-light-svg-icons';
import { actions as MovingJobEstimateActions } from '../../Ducks/MovingJobEstimate.duck';
import { actions as FirstStepActions } from '../../../FirstStep/Ducks/FirstStep.duck';
import { actions as SecondStepActions } from '../../../SecondStep/Ducks/SecondStep.duck';

const useStyles = makeStyles(() => ({
  addListItem: {
    cursor: 'pointer',
    height: '64px',
    boxSizing: 'border-box',
    marginTop: '10px',
    border: '2px dashed #C6C6C6',
    background: 'transparent',
    borderRadius: '5px',
  },
  listItem: {
    cursor: 'pointer',
    height: '64px',
    boxSizing: 'border-box',
    marginTop: '10px',
    backgroundColor: '#ffffff',
    borderRadius: '5px',
  },
  roomTitle: {
    fontWeight: 'bold',
  },
  dialogContent: {
    maxHeight: 'calc(100% - 64px)',
    overflow: 'scroll',
  },
  roomTitleFinished: {
    fontWeight: 'bold',
    color: '#A3A1AE',
  },
  roomIcon: {
    marginRight: '19px',
  },
}));

const RoomSelectionPage = ({ handleData }) => {
  const secondSteps = useSelector(state => state.SecondStepReducer.items);
  const selectedRoom = useSelector(state => state.SecondStepReducer.propSelected);
  const {
    rooms: roomsList,
    loadedInformation: { roomTypes },
  } = useSelector(state => state.MovingJobEstimateReducer);
  const dispatch = useDispatch();
  const classes = useStyles();

  const [openDialog, setOpenDialog] = useState(false);
  const [roomDialogOpen, setRoomDialogOpen] = useState(false);
  const [selectedRooms, setSelectedRooms] = useState([]);
  const [selectedRoomTitle, setSelectedRoomTitle] = useState('');

  useEffect(() => {
    const newSelectedRooms = roomsList
      .map((item, roomIndex) => ({ ...item, roomIndex }))
      .filter(item => item.selected)
      .sort((a, b) => {
        if (a.name < b.name) return -1;
        if (a.name > b.name) return 1;
        return 0;
      });

    setSelectedRooms(newSelectedRooms);
  }, [roomsList]);

  const roomClicked = (item, index) => {
    const makeCall = async () => {
      await dispatch(SecondStepActions.setPropSelected({ value: item.value, index }, item.furnitureType));
      dispatch(FirstStepActions.setNav({ disabledSwipe: true }));
      dispatch(SecondStepActions.setNav({ disabledSwipe: false }));
      setSelectedRoomTitle(item.label);
      setOpenDialog(true);
    };
    makeCall();
  };

  const checkCompleted = () => {
    const completed = secondSteps.filter(step => !step.completed).length === 0;
    const newRoomsList = [...roomsList];
    newRoomsList[selectedRoom.index] = { ...roomsList[selectedRoom.index], completed };

    dispatch(MovingJobEstimateActions.saveRooms(newRoomsList));
  };

  const handleAddRoom = addedItem => {
    const optionUnselected = roomsList.findIndex(item => item.selected !== true && item.name === addedItem.name);
    const newList = [...roomsList];
    if (optionUnselected !== -1) {
      const newOption = { ...newList[optionUnselected], selected: true };
      newList[optionUnselected] = newOption;
    } else {
      const optionsSelected = roomsList.filter(item => item.selected === true && item.name === addedItem.name);
      newList.push({ ...addedItem, selected: true, index: optionsSelected.length });
    }
    dispatch(MovingJobEstimateActions.saveRooms(newList));
    setRoomDialogOpen(false);
  };

  useEffect(() => {
    const allRoomsFinished = selectedRooms.filter(room => room.completed).length === selectedRooms.length;

    dispatch(FirstStepActions.filledForm(allRoomsFinished));

    if (handleData) {
      handleData(selectedRooms);
    }
  }, [selectedRooms, dispatch, handleData]);

  return (
    <Grid container spacing={4}>
      <Grid item xs={12}>
        <Typography variant="h3" component="h3" align="center">
          <FormattedMessage id="room.furniture_inside_room" />
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <List>
          {selectedRooms.map(
            item =>
              item.selected && (
                <ListItem
                  key={`${item.value}${item.roomIndex}`}
                  className={classes.listItem}
                  onClick={() => roomClicked(item, item.roomIndex)}
                >
                  <FontAwesomeIcon
                    size="lg"
                    color={item.completed ? '#A3A1AE' : null}
                    icon={['fal', item.icon.replace('fa-', '')]}
                    className={classes.roomIcon}
                  />
                  <ListItemText
                    primary={`${item.label} ${!item.index ? '' : `#${item.index + 1}`}`}
                    primaryTypographyProps={{
                      className: item.completed ? classes.roomTitleFinished : classes.roomTitle,
                    }}
                  />
                  {item.completed && (
                    <ListItemIcon>
                      <SvgIcon height={25} width={25}>
                        <path
                          d="M24.8847 2.64706C24.8847 2.64706 24.8847 2.63655 24.8742 2.63655C24.5484 1.18697 23.6026 0.294118 22.1104 0.0630252C21.8582 0.0210084 21.606 0 21.3538 0C20.3555 0 19.4307 0.304622 18.8317 0.546218C15.2167 1.94328 11.9696 4.30672 9.17429 7.55252C8.53327 8.29832 7.64004 9.47479 7.28274 10.9769C6.93596 12.4685 7.48241 13.7605 8.73293 14.4643C7.53495 15.1891 6.32646 15.9244 5.12848 16.6492C3.6993 17.521 2.27013 18.3824 0.840961 19.2542C0.662314 19.3697 0.38909 19.5273 0.199935 19.8004C0.0528146 20.021 -0.146849 20.4832 0.168409 21.0819C0.399599 21.5231 0.798926 21.7752 1.27181 21.7752C1.56606 21.7752 1.87081 21.6807 2.19657 21.4811C3.93049 20.4097 5.66442 19.3487 7.39834 18.2773C9.02717 17.2794 10.656 16.271 12.2848 15.2731C12.3059 15.2626 12.3374 15.2416 12.3584 15.2311C12.3899 15.2101 12.4215 15.1891 12.4425 15.1786C13.714 15.1786 15.1957 15.2521 16.5513 15.7878C16.6354 15.8193 16.6985 15.8508 16.751 15.8719C16.73 15.9139 16.688 15.9664 16.6354 16.0399C16.3096 16.4496 15.9524 16.8172 15.5635 17.2164C15.3323 17.458 15.0906 17.6996 14.8595 17.9517C14.6703 18.1618 14.4601 18.3824 14.2605 18.5924C13.693 19.1912 13.115 19.8004 12.6316 20.5147C12.1587 21.2185 12.0326 22.0378 12.2848 22.8256C12.5476 23.6555 13.1991 24.3067 14.0608 24.6324C14.3866 24.7584 14.7123 24.8214 15.0171 24.895C15.1432 24.9265 15.2693 24.9475 15.3954 24.979L15.4795 25H15.574H16.9297H17.0347L17.1398 24.9685C17.1819 24.958 17.2134 24.9475 17.2554 24.9265C17.2869 24.916 17.329 24.9055 17.35 24.895L17.5181 24.8634C18.4534 24.7059 19.4202 24.5378 20.3765 24.3803C20.8704 24.2962 21.2277 24.0966 21.4378 23.7815C21.5745 23.5819 21.7111 23.2458 21.5955 22.7731C21.4484 22.1534 20.9755 21.7857 20.3134 21.7857C20.1873 21.7857 20.0612 21.7962 19.8931 21.8172C18.527 22.0168 17.1924 22.2164 15.8578 22.3319C15.8368 22.3319 15.8052 22.3319 15.7842 22.3319C15.4584 22.3319 15.0276 22.1639 14.891 22.1113C14.8384 22.0903 14.8069 22.0693 14.7964 22.0483C14.7859 22.0273 14.7859 21.9538 14.8279 21.8592C14.912 21.6807 15.0066 21.5441 15.0906 21.4496C15.3954 21.1345 15.7212 20.8088 16.0364 20.4937C16.4673 20.063 16.9086 19.6218 17.329 19.1702L17.413 19.0861C17.9385 18.5294 18.4849 17.9517 18.9158 17.2689C19.3677 16.5651 19.5148 15.8403 19.3256 15.1786C19.1365 14.4958 18.632 13.9286 17.8544 13.5294C17.4025 13.2983 16.8666 13.1092 16.2781 12.9727C16.131 12.9412 15.9839 12.9097 15.8473 12.8782C16.152 12.6576 16.4568 12.4475 16.751 12.2269C18.1802 11.1975 19.6619 10.1366 21.07 9.02311C22.6989 7.73109 24.2962 6.22899 24.9687 3.96008L25.0003 3.85504V3.75V3.01471V2.80462L24.8847 2.64706ZM22.226 3.85504C21.7846 4.84244 21.007 5.59874 20.1873 6.36555C17.991 8.38235 15.532 10.042 12.7787 11.8487C12.7367 11.8697 12.6947 11.9013 12.6632 11.9223C12.5476 11.9958 12.4109 12.0798 12.2743 12.1954C11.9906 12.437 11.6964 12.5525 11.3601 12.5525C10.9082 12.5525 10.3933 12.3529 9.95193 12.1744C9.85735 12.1324 9.74176 12.0903 9.7943 11.7647C9.95193 10.8298 10.4774 10.0525 11.2235 9.18067C13.4513 6.56513 16.11 4.56933 19.1154 3.2458C19.6619 3.0042 20.2714 2.84664 20.7128 2.73109C20.8389 2.69958 20.9545 2.66807 21.0595 2.63655C21.669 2.65756 22.1209 2.80462 22.2785 3.05672C22.4467 3.28782 22.3206 3.65546 22.226 3.85504Z"
                          fill="#3A78F2"
                        />
                      </SvgIcon>
                    </ListItemIcon>
                  )}
                </ListItem>
              ),
          )}

          <ListItem className={classes.addListItem} onClick={() => setRoomDialogOpen(true)}>
            <FontAwesomeIcon size="lg" icon={faPlus} className={classes.roomIcon} />
            <ListItemText
              primary={<FormattedMessage id="room.add_room" />}
              primaryTypographyProps={{
                className: classes.roomTitle,
              }}
            />
          </ListItem>
        </List>
        <DialogComponent open={openDialog} handleClose={() => setOpenDialog(false)}>
          <DragToCloseModalComponent closeFunction={() => setOpenDialog(false)} />
          <WizardComponent
            mapper={formMapper}
            reducer={state => state.SecondStepReducer}
            formKey="secondStepForm"
            backFunction={(active, selected) => dispatch(SecondStepActions.prev(active, selected))}
            nextFunction={(active, selected) => dispatch(SecondStepActions.next(active, selected))}
            loadingFunction={data => dispatch(SecondStepActions.setLoading(data))}
          />
          <FooterComponent
            disableDesktop
            nextButtonId="footerNextButtonDialog"
            prevButtonId="footerPrevButtonDialog"
            reducer={state => state.SecondStepReducer}
            backFunction={(active, selected) => dispatch(SecondStepActions.prev(active, selected))}
            nextFunction={(active, selected) => dispatch(SecondStepActions.next(active, selected))}
            loadingFunction={data => dispatch(SecondStepActions.setLoading(data))}
            finishFunction={() => {
              checkCompleted();
              setOpenDialog(false);
              dispatch(FirstStepActions.setNav({ disabledSwipe: false }));
              dispatch(SecondStepActions.setNav({ disabledSwipe: true }));
            }}
            color="#021B4E"
            title={selectedRoomTitle}
          />
        </DialogComponent>
        <DialogComponent
          open={roomDialogOpen}
          handleClose={() => setRoomDialogOpen(false)}
          title={<FormattedMessage id="room.choose_the_room_to_add" />}
        >
          <DragToCloseModalComponent closeFunction={() => setRoomDialogOpen(false)} />
          <Grid container spacing={2}>
            {roomTypes.length > 0 &&
              roomTypes.map(item => (
                <Grid item xs={6} sm={4} key={item.id}>
                  <IconCardComponent item={item} clickFunction={insItem => handleAddRoom(insItem)} />
                </Grid>
              ))}
          </Grid>
        </DialogComponent>
      </Grid>
    </Grid>
  );
};

export default RoomSelectionPage;
